import React, { useState } from 'react';
import { Tooltip } from '../Tooltip';
import ChatHeader from './components/ChatHeader';
import ChatMessages from './components/ChatMessages';
import ChatInput from './components/ChatInput';
import { useConversation } from './hooks/useConversation';
import styles from './styles/AIChat.module.css';

const AIChat = () => {
  const [isMinimized, setIsMinimized] = useState(false);
  
const {
  messages,
  inputMessage,
  setInputMessage,
  isTyping,
  chatEnded,
  sendingLead, 
  userInfo,
  sendMessage
} = useConversation();

  return (
    <section id="contact" className="section bg-primary hero-mask">
      <div className="container pt-5 mt-5">
        <div className="row">
          <div className="col-lg-5 text-center text-lg-start wow fadeInUp">
            <h2 className="text-10 fw-600 mb-5">Momente. Bilder. Emotionen.</h2>
            <p className="text-5 mb-5">
              Chatte mit meinem AI-Assistenten und erhalte ein personalisiertes Angebot für Dein Fotoshooting. Powered by OpenAI.
            </p>
            <h3 className="text-5 fw-600">Adresse</h3>
            <address className="text-4">
              Sagenhofstrasse 18, 6030 Ebikon, Schweiz
            </address>
            <h3 className="text-5 fw-600">Call:</h3>
            <p className="text-4">+41 nur auf Anfrage</p>
            <ul className="social-icons social-icons-lg justify-content-center justify-content-lg-start mt-5">
              <li className="social-icons-facebook">
                <Tooltip text="Facebook" placement="top">
                  <a
                    href="http://www.facebook.com/arnetphoto/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="fab fa-facebook" />
                  </a>
                </Tooltip>
              </li>
              <li className="social-icons-instagram">
                <Tooltip text="Instagram" placement="top">
                  <a
                    href="http://www.instagram.com/ivo.arnet.photography/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="fab fa-instagram" />
                  </a>
                </Tooltip>
              </li>
              <li className="social-icons-linkedin">
                <Tooltip text="Linkedin" placement="top">
                  <a
                    href="https://www.linkedin.com/company/arnet-photography/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="fab fa-linkedin-in" />
                  </a>
                </Tooltip>
              </li>
              <li className="social-icons-500px">
                <Tooltip text="500px" placement="top">
                  <a
                    href="https://500px.com/arnetphotography"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="fab fa-500px" />
                  </a>
                </Tooltip>
              </li>
            </ul>
          </div>
          
          <div className="col-lg-6 ms-auto mt-5 mt-lg-0 wow fadeInUp" data-wow-delay="0.3s">
            <div className={styles.aiChatContainer}>
              <ChatHeader 
                isMinimized={isMinimized} 
                setIsMinimized={setIsMinimized} 
              />

              {!isMinimized && (
                <>
                  <ChatMessages 
                    messages={messages} 
                    isTyping={isTyping} 
                  />

                  <ChatInput
                    inputMessage={inputMessage}
                    setInputMessage={setInputMessage}
                    sendMessage={sendMessage}
                    isTyping={isTyping}
                    chatEnded={chatEnded}
                    sendingLead={sendingLead} 
                  />
                </>
              )}
            </div>

            {/* Debug panel - remove in production */}
            {process.env.NODE_ENV === 'development' && (
              <div className="mt-3 p-3 bg-light rounded">
                <h6>Gesammelte Informationen:</h6>
                <pre>{JSON.stringify(userInfo, null, 2)}</pre>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Global styles for chat components */}
      <style jsx global>{`
        .chat-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding: 1rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .chat-avatar {
          width: 40px;
          height: 40px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .chat-messages {
          flex: 1;
          padding: 1rem;
          overflow-y: auto;
          background: linear-gradient(to bottom, #f8f9fa, #e9ecef);
        }

        .message {
          margin-bottom: 1rem;
          display: flex;
          animation: fadeInUp 0.3s ease-out;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .user-message {
          justify-content: flex-end;
        }

        .bot-message {
          justify-content: flex-start;
        }

        .message-content {
          max-width: 85%;
          padding: 0.75rem 1rem;
          border-radius: 18px;
          position: relative;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .user-message .message-content {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-bottom-right-radius: 4px;
        }

        .bot-message .message-content {
          background: white;
          color: #333;
          border: 1px solid #e9ecef;
          border-bottom-left-radius: 4px;
        }

        .message-time {
          opacity: 0.7;
          font-size: 0.75rem;
        }

        .typing-indicator {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .typing-indicator span {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: #667eea;
          animation: typing 1.4s infinite ease-in-out;
        }

        .typing-indicator span:nth-child(1) { animation-delay: -0.32s; }
        .typing-indicator span:nth-child(2) { animation-delay: -0.16s; }

        @keyframes typing {
          0%, 80%, 100% { transform: scale(0); opacity: 0.5; }
          40% { transform: scale(1); opacity: 1; }
        }

        .chat-input {
          padding: 1rem;
          background: white;
          border-top: 1px solid #e9ecef;
        }

        .chat-input .form-control {
          border: 1px solid #ddd;
          border-radius: 25px;
          padding: 0.75rem 1rem;
          transition: border-color 0.3s ease;
        }

        .chat-input .form-control:focus {
          border-color: #667eea;
          box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
        }

        .chat-input .btn {
          border-radius: 25px;
          padding: 0.75rem 1rem;
          margin-left: 0.5rem;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;
          transition: transform 0.2s ease;
        }

        .chat-input .btn:hover {
          transform: translateY(-1px);
        }

        .chat-input .btn:disabled {
          opacity: 0.6;
          transform: none;
        }

        .chat-messages::-webkit-scrollbar {
          width: 6px;
        }

        .chat-messages::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }

        .chat-messages::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 3px;
        }

        .chat-messages::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }

        @media (max-width: 768px) {
          .ai-chat-container {
            height: 500px;
          }
          
          .message-content {
            max-width: 90%;
          }
        }
      `}</style>
    </section>
  );
};

export default AIChat;
